import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Youtube, Phone, MessageCircle, Facebook, AtSign } from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import InstallAppButton from "@/components/InstallAppButton";
import { SUPPORT_PHONE, SUPPORT_PHONE_DISPLAY, SUPPORT_EMAIL, buildSupportWhatsAppLink } from "@/lib/support";

// Central place to update social handles once the accounts exist.
// If any URL is left as null the icon is hidden — clean footer, no dead links.
const SOCIAL_LINKS = {
  instagram: "https://instagram.com/thehostelnow",
  facebook:  "https://facebook.com/thehostelnow",
  twitter:   "https://x.com/thehostelnow",
  threads:   "https://www.threads.net/@thehostelnow",
  youtube:   null,
};

export default function Footer() {
  return (
    <footer className="mt-24 border-t border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-5 gap-8">
        <div>
          <LogoLockup size={40} tld={false} />
          <p className="mt-4 text-sm text-slate-600 leading-relaxed max-w-xs">
            Verified Stays. No Hidden Charges. Ever. Beds from ₹250 near every major coaching center.
          </p>
          <div className="mt-4">
            <InstallAppButton variant="hero" className="!h-10 !px-4 text-sm" />
          </div>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Cities</h4>
          <ul className="mt-4 space-y-2 text-sm">
            {[
              ["Kota", "kota"], ["Jaipur", "jaipur"], ["Sikar", "sikar"],
              ["Ajmer", "ajmer"], ["Udaipur", "udaipur"], ["Jodhpur", "jodhpur"],
              ["Delhi (Mukherjee Nagar)", "delhi"],
            ].map(([label, slug]) => (
              <li key={slug}>
                <Link to={`/${slug}`} className="hover:text-orange-800 transition-colors">{label}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Company</h4>
          <ul className="mt-4 space-y-2 text-sm">
            <li>About</li><li>Careers</li><li>Contact</li>
            <li><Link to="/signup?role=owner" className="hover:text-orange-800 transition-colors font-semibold">List your hostel →</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Support</h4>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <a data-testid="footer-support-phone" href={`tel:${SUPPORT_PHONE}`} className="inline-flex items-center gap-1.5 font-semibold text-orange-800 hover:text-orange-900 transition-colors">
                <Phone size={14} /> {SUPPORT_PHONE_DISPLAY}
              </a>
            </li>
            <li>
              <a data-testid="footer-support-whatsapp" href={buildSupportWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-emerald-700 hover:text-emerald-800 transition-colors">
                <MessageCircle size={14} /> WhatsApp us
              </a>
            </li>
            <li>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-orange-800 transition-colors break-all text-xs">
                {SUPPORT_EMAIL}
              </a>
            </li>
            <li className="text-xs text-slate-500">Reply within 24 hours</li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Follow</h4>
          <div className="flex gap-3 mt-4">
            {SOCIAL_LINKS.instagram && (
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram" className="w-9 h-9 grid place-items-center rounded-full bg-bandhani-500 text-white hover:bg-bandhani-600 transition-colors"><Instagram size={16} /></a>
            )}
            {SOCIAL_LINKS.facebook && (
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook" className="w-9 h-9 grid place-items-center rounded-full bg-jodhpur-600 text-white hover:bg-jodhpur-700 transition-colors"><Facebook size={16} /></a>
            )}
            {SOCIAL_LINKS.twitter && (
              <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer" data-testid="social-twitter" aria-label="X (Twitter)" className="w-9 h-9 grid place-items-center rounded-full bg-stone-900 text-white hover:bg-stone-950 transition-colors"><Twitter size={16} /></a>
            )}
            {SOCIAL_LINKS.threads && (
              <a href={SOCIAL_LINKS.threads} target="_blank" rel="noopener noreferrer" data-testid="social-threads" aria-label="Threads" className="w-9 h-9 grid place-items-center rounded-full bg-stone-800 text-white hover:bg-stone-900 transition-colors"><AtSign size={16} /></a>
            )}
            {SOCIAL_LINKS.youtube && (
              <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" data-testid="social-youtube" aria-label="YouTube" className="w-9 h-9 grid place-items-center rounded-full bg-vermilion-600 text-white hover:bg-vermilion-700 transition-colors"><Youtube size={16} /></a>
            )}
          </div>
          <div className="mt-2 text-[10px] text-slate-500">Handle: @thehostelnow</div>
        </div>
      </div>
      <div className="border-t border-slate-200 py-5 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} TheHostelNow.com — Built with ♥ for Indian students.
      </div>
    </footer>
  );
}
